import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const products = [
  {
    icon: 'ri-share-line',
    title: 'Interconnexion',
    badge: 'Réseau',
    image:
      'https://readdy.ai/api/search-image?query=Futuristic%20network%20interconnection%20technology%20glowing%20fiber%20optic%20cables%20green%20orange%20lights%20connecting%20servers%20data%20center%20professional%20enterprise%20connectivity%20infrastructure%20dark%20background&width=800&height=500&seq=promo-interconnect-1&orientation=landscape',
    description:
      'Reliez tous vos sites distants avec nos solutions VPN MPLS, IPSEC et liens dédiés. Haute disponibilité 99.9% garantie.',
    link: '/reseaux',
    color: 'from-primary to-primary-dark',
    accent: '#10B981',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'MATRIIX ERP',
    badge: 'Gestion',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20ERP%20software%20dashboard%20modern%20interface%20financial%20charts%20analytics%20business%20management%20modules%20green%20orange%20accent%20colors%20dark%20sleek%20design%20enterprise%20resource%20planning%20system&width=800&height=500&seq=promo-erp-1&orientation=landscape',
    description:
      'Pilotez toute votre entreprise depuis une seule plateforme : comptabilité, commercial, production et tableaux de bord temps réel.',
    link: '/gestion',
    color: 'from-secondary to-secondary-dark',
    accent: '#F97316',
  },
  {
    icon: 'ri-database-2-line',
    title: 'Base de Données',
    badge: 'Données',
    image:
      'https://readdy.ai/api/search-image?query=Advanced%20database%20management%20system%20SQL%20server%20architecture%20data%20flow%20visualization%20green%20orange%20highlights%20dark%20background%20enterprise%20data%20security%20backup%20optimization%20professional%20infrastructure&width=800&height=500&seq=promo-db-1&orientation=landscape',
    description:
      'SQL Server, MySQL, PostgreSQL — conception, administration, optimisation et sécurité de vos données critiques.',
    link: '/base-de-donnees',
    color: 'from-primary-light to-primary',
    accent: '#059669',
  },
];

const STORAGE_KEY = 'sig_promo_last_shown';
const SHOW_INTERVAL_MS = 10 * 60 * 1000; // re-affiche après 10 min

const PromoPopup = () => {
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const lastShown = Number(sessionStorage.getItem(STORAGE_KEY) || 0);
    const now = Date.now();
    const delay = now - lastShown > SHOW_INTERVAL_MS ? 5000 : -1;
    if (delay < 0) return;

    const timer = setTimeout(() => {
      setVisible(true);
      sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % products.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + products.length) % products.length);
  }, []);

  // Auto-avance toutes les 3s sauf si on survole
  useEffect(() => {
    if (!visible || paused) return;
    const interval = setInterval(next, 3000);
    return () => clearInterval(interval);
  }, [visible, paused, next]);

  if (!visible) return null;

  const product = products[current];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="promo-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={() => setVisible(false)}
        >
          <motion.div
            key="promo-card"
            initial={{ scale: 0.85, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
            style={{ background: '#0f1117' }}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {/* Header badge */}
            <div
              className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 z-10"
              style={{ background: 'rgba(0,0,0,0.55)' }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ background: product.accent }}
                />
                <span className="text-xs font-semibold uppercase tracking-widest text-white/70">
                  Offre Spéciale SIG SARL
                </span>
              </div>
              <button
                onClick={() => setVisible(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Image + overlay */}
            <div className="relative h-56 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={`img-${current}`}
                  src={product.image}
                  alt={product.title}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(15,17,23,0.3) 0%, rgba(15,17,23,0.85) 100%)',
                }}
              />

              {/* Product badge */}
              <div className="absolute top-12 left-5">
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold text-white"
                  style={{ background: product.accent }}
                >
                  {product.badge}
                </span>
              </div>

              {/* Arrows */}
              <button
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/70 transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/70 transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 pt-5 pb-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${current}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-xl"
                      style={{ background: `${product.accent}22` }}
                    >
                      <i className={`${product.icon} text-xl`} style={{ color: product.accent }} />
                    </div>
                    <h3 className="text-xl font-bold text-white">{product.title}</h3>
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed mb-5">
                    {product.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* CTA + dots */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {products.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrent(i)}
                      className="rounded-full transition-all cursor-pointer"
                      style={{
                        width: i === current ? 24 : 8,
                        height: 8,
                        background: i === current ? product.accent : 'rgba(255,255,255,0.2)',
                      }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setVisible(false)}
                    className="text-sm text-white/40 hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Fermer
                  </button>
                  <Link
                    to={product.link}
                    onClick={() => setVisible(false)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 whitespace-nowrap"
                    style={{ background: product.accent }}
                  >
                    Découvrir
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PromoPopup;
